import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, Calendar, Share2, Twitter, Instagram } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { ArticleCard } from "@/components/ArticleCard";

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // メイン記事の取得
  const { data: article, isLoading, error } = useQuery({
    queryKey: ["article", id],
    queryFn: async () => {
      if (!id) throw new Error("記事IDが見つかりません");
      
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (!data) throw new Error("記事が見つかりません");
      return data;
    },
    enabled: !!id,
  });

  // 関連記事の取得
  const { data: relatedArticles } = useQuery({
    queryKey: ["related-articles", article?.category],
    queryFn: async () => {
      if (!article?.category) return [];
      
      const { data } = await supabase
        .from("articles")
        .select("*")
        .eq("category", article.category)
        .neq("id", id)
        .limit(3);

      return data || [];
    },
    enabled: !!article?.category,
  });

  // SNSシェア機能
  const shareUrl = window.location.href;
  const shareTitle = article?.title || "";

  const handleShare = (platform: string) => {
    let shareLink = "";
    switch (platform) {
      case "twitter":
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "instagram":
        navigator.clipboard.writeText(`${shareTitle}\n${shareUrl}`);
        toast({
          title: "コピーしました",
          description: "Instagramで共有するためにテキストをコピーしました。",
        });
        return;
    }
    window.open(shareLink, "_blank");
  };

  React.useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "エラーが発生しました",
        description: error instanceof Error ? error.message : "記事の取得に失敗しました",
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-foreground/10 rounded w-3/4 mx-auto mb-4" />
          <div className="h-4 bg-foreground/10 rounded w-1/2 mx-auto mb-8" />
          <div className="space-y-4">
            <div className="h-4 bg-foreground/10 rounded" />
            <div className="h-4 bg-foreground/10 rounded" />
            <div className="h-4 bg-foreground/10 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4 text-foreground">記事が見つかりません</h1>
        <Button onClick={() => navigate("/")} variant="outline">
          トップページに戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* 光の帯 / ノイズオーバーレイ（Rooms/Topと統一） */}
      <div className="light-band" />
      <div className="noise-overlay" />
      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* ヒーローセクション */}
        <div className="mb-8 glass-card p-8 rounded-lg">
          <h1 className="text-4xl font-bold mb-4 text-foreground">
            {article.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-foreground/80 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(article.created_at), "yyyy年MM月dd日")}</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>{article.content.length} 文字</span>
            </div>
            <Badge variant="outline" className="text-foreground/80 border-[rgba(255,255,255,0.12)] hover:bg-white/10">
              {article.category}
            </Badge>
          </div>
        </div>

        {/* メインコンテンツ */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <article className="lg:col-span-3 prose prose-elegant max-w-none">
            {article.image_url && (
              <div className="relative h-96 mb-8 rounded-lg overflow-hidden elegant-shadow">
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="object-cover w-full h-full"
                />
              </div>
            )}
            
            <div className="leading-relaxed whitespace-pre-wrap p-8 rounded-lg elegant-shadow
              bg-[rgba(8,20,20,0.55)] backdrop-blur-md border border-[rgba(255,255,255,0.06)] text-foreground">
              {article.content}
            </div>

            {/* SNSシェアボタン */}
            <div className="mt-12 flex items-center gap-4 p-6 rounded-lg elegant-shadow
              bg-[rgba(8,20,20,0.55)] backdrop-blur-md border border-[rgba(255,255,255,0.06)]">
              <span className="flex items-center gap-2 text-foreground/80">
                <Share2 className="w-5 h-5" />
                シェアする：
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare("twitter")}
                className="hover:text-foreground hover:border-foreground/40"
              >
                <Twitter className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleShare("instagram")}
                className="hover:text-foreground hover:border-foreground/40"
              >
                <Instagram className="w-5 h-5" />
              </Button>
            </div>
          </article>

          {/* サイドバー：関連記事 */}
          <aside className="lg:col-span-1">
            <h2 className="text-xl font-bold mb-6 text-foreground">関連記事</h2>
            <div className="space-y-6">
              {relatedArticles?.map((relatedArticle) => (
                <ArticleCard
                  key={relatedArticle.id}
                  id={relatedArticle.id}
                  title={relatedArticle.title}
                  category={relatedArticle.category}
                  excerpt={relatedArticle.excerpt}
                  imageUrl={relatedArticle.image_url || undefined}
                />
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;